import { FormEvent, useMemo, useState } from "react";
import { useAuth } from "../providers/authProvider";
import { useData } from "../providers/dataProvider";
import { CONTEXTS, Context as TodoContext, TodoData } from "../../../types";

const getTodayLocal = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const topOpenTodo = (todos: TodoData[], categoryId: string | undefined) =>
  todos
    .filter((t) => !t.done && !t.scheduledDate && t.categoryId === categoryId)
    .sort((a, b) => a.order - b.order)[0];

export const Dashboard = () => {
  const { logout } = useAuth();
  const { todos, categories, loading, createTodo, updateTodo, deleteTodo, createCategory } =
    useData();
  const [contextFilter, setContextFilter] = useState<TodoContext | "all">("all");
  const [title, setTitle] = useState("");
  const [newCategoryName, setNewCategoryName] = useState("");
  const today = getTodayLocal();

  const visibleTodos = useMemo(
    () =>
      contextFilter === "all"
        ? todos
        : todos.filter((t) => t.context === contextFilter),
    [todos, contextFilter],
  );

  const todayTodos = useMemo(
    () =>
      visibleTodos
        .filter((t) => !t.done && t.scheduledDate && t.scheduledDate <= today)
        .sort((a, b) => a.order - b.order),
    [visibleTodos, today],
  );

  const inboxTodos = useMemo(
    () =>
      visibleTodos
        .filter((t) => !t.done && !t.categoryId && !t.scheduledDate)
        .sort((a, b) => a.order - b.order),
    [visibleTodos],
  );

  const capture = async (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    await createTodo({
      title: title.trim(),
      context: contextFilter === "all" ? CONTEXTS.WORK : contextFilter,
    });
    setTitle("");
  };

  const addCategory = async (e: FormEvent) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;
    await createCategory(newCategoryName.trim());
    setNewCategoryName("");
  };

  const scheduleTopOf = (categoryId: string | undefined) => {
    const top = topOpenTodo(visibleTodos, categoryId);
    if (!top) return;
    updateTodo(top, {
      scheduledDate: today,
      timesScheduled: top.timesScheduled + 1,
    });
  };

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-8">
      <header className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">todoit</h1>
        <div className="flex items-center gap-2">
          {(["all", CONTEXTS.WORK, CONTEXTS.HOME] as const).map((c) => (
            <button
              key={c}
              onClick={() => setContextFilter(c)}
              className={`px-3 py-1 rounded text-sm capitalize ${
                contextFilter === c
                  ? "bg-neutral-900 text-white"
                  : "bg-neutral-100 text-neutral-700"
              }`}
            >
              {c}
            </button>
          ))}
          <button onClick={logout} className="text-sm text-neutral-500 ml-2">
            Log out
          </button>
        </div>
      </header>

      <form onSubmit={capture} className="flex gap-2">
        <input
          className="flex-1 border rounded px-3 py-2"
          placeholder="Throw something in…"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <button className="bg-neutral-900 text-white rounded px-4">Add</button>
      </form>

      {loading ? (
        <p className="text-neutral-500">Loading…</p>
      ) : (
        <>
          <section>
            <h2 className="text-sm font-semibold text-neutral-500 mb-2">Today</h2>
            <TodoList
              todos={todayTodos}
              onToggle={(t) => updateTodo(t, { done: !t.done })}
              onDelete={deleteTodo}
              empty="Nothing scheduled yet."
            />
          </section>

          <section>
            <h2 className="text-sm font-semibold text-neutral-500 mb-2">Inbox</h2>
            <TodoList
              todos={inboxTodos}
              onToggle={(t) => updateTodo(t, { done: !t.done })}
              onDelete={deleteTodo}
              empty="Inbox is empty."
            />
          </section>

          {categories.map((category) => {
            const categoryTodos = visibleTodos
              .filter((t) => !t.done && t.categoryId === category.id)
              .sort((a, b) => a.order - b.order);
            return (
              <section key={category.id}>
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-sm font-semibold text-neutral-500">
                    {category.name}
                  </h2>
                  <button
                    onClick={() => scheduleTopOf(category.id)}
                    className="text-xs text-neutral-500 underline"
                  >
                    Work on this today
                  </button>
                </div>
                <TodoList
                  todos={categoryTodos}
                  onToggle={(t) => updateTodo(t, { done: !t.done })}
                  onDelete={deleteTodo}
                  empty="Nothing here yet."
                />
              </section>
            );
          })}

          <form onSubmit={addCategory} className="flex gap-2 pt-4 border-t">
            <input
              className="flex-1 border rounded px-3 py-2 text-sm"
              placeholder="New category / project…"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
            />
            <button className="text-sm bg-neutral-100 rounded px-3">
              Add category
            </button>
          </form>
        </>
      )}
    </div>
  );
};

const TodoList = ({
  todos,
  onToggle,
  onDelete,
  empty,
}: {
  todos: TodoData[];
  onToggle: (todo: TodoData) => void;
  onDelete: (todo: TodoData) => void;
  empty: string;
}) => {
  if (!todos.length) {
    return <p className="text-sm text-neutral-400">{empty}</p>;
  }
  return (
    <ul className="space-y-1">
      {todos.map((todo) => (
        <li
          key={todo.id}
          className="flex items-center gap-2 bg-white border rounded px-3 py-2"
        >
          <input
            type="checkbox"
            checked={todo.done}
            onChange={() => onToggle(todo)}
          />
          <span className="flex-1 text-sm">{todo.title}</span>
          <button
            onClick={() => onDelete(todo)}
            className="text-xs text-neutral-400"
          >
            delete
          </button>
        </li>
      ))}
    </ul>
  );
};
